import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import '../styles/design-system.css';
import '../styles/pages.css';

const IP = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = {
  Plus: (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M5 12h14M12 5v14" /></svg>),
  Globe: (p) => (<svg width="40" height="40" {...IP} strokeWidth="1.5" {...p}><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" /></svg>),
  Check: (p) => (<svg width="11" height="11" {...IP} strokeWidth="2.5" {...p}><polyline points="20 6 9 17 4 12" /></svg>),
  Clock: (p) => (<svg width="11" height="11" {...IP} {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>),
  Trash: (p) => (<svg width="14" height="14" {...IP} {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>),
  Copy: (p) => (<svg width="14" height="14" {...IP} {...p}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>),
  Refresh: (p) => (<svg width="12" height="12" {...IP} {...p}><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>),
  Drop: (p) => (<svg width="18" height="18" {...IP} {...p}><path d="M12 2s-6 6-6 11a6 6 0 0 0 12 0c0-5-6-11-6-11z" /></svg>),
};

export default function DomainsPage() {
  const { workspace } = useAuth();
  const [domains, setDomains] = useState([]);
  const [subdomain, setSubdomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [addingDomain, setAddingDomain] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [verifying, setVerifying] = useState({});

  useEffect(() => {
    if (!workspace?.workspace_id) return;
    fetchDomains();
    setSubdomain(workspace.subdomain || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.workspace_id]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/workspaces/${workspace.workspace_id}/domains`);
      setDomains(res.data || []);
    } catch {
      toast.error('Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      await api.post(`/workspaces/${workspace.workspace_id}/domains`, {
        domain: newDomain.trim().toLowerCase(),
      });
      toast.success('Domain added — add the CNAME record to verify it');
      setNewDomain('');
      setAddingDomain(false);
      fetchDomains();
    } catch (err) {
      toast.error(err.message || 'Failed to add domain');
    }
  };

  const handleVerify = async (domain) => {
    try {
      setVerifying((v) => ({ ...v, [domain.domain_id]: true }));
      await api.post(`/workspaces/${workspace.workspace_id}/domains/${domain.domain_id}/verify`, {});
      toast.success('Domain verified!');
      fetchDomains();
    } catch {
      toast.error('Verification failed — ensure your CNAME record is live');
    } finally {
      setVerifying((v) => ({ ...v, [domain.domain_id]: false }));
    }
  };

  const handleDelete = async (domainId) => {
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/domains/${domainId}`);
      setDomains((d) => d.filter((x) => x.domain_id !== domainId));
      toast.success('Domain removed');
    } catch {
      toast.error('Failed to remove domain');
    }
  };

  const inputStyle = {
    padding: '9px 12px',
    background: 'var(--surface)',
    border: '1px solid var(--line-strong)',
    borderRadius: 9,
    fontSize: 14,
    color: 'var(--ink)',
    fontFamily: 'inherit',
    outline: 'none',
    flex: 1,
  };

  return (
    <div className="ds-page" data-testid="domains-page">
      <div className="page-header">
        <div>
          <div className="page-label mustard">Workspace · {workspace?.name || 'your workspace'}</div>
          <h1 className="page-title">Custom <em>domains</em>.</h1>
          <p className="page-sub">Manage the branded short domains you use across LinkFlow.</p>
        </div>
        <button
          type="button"
          className="btn-coral"
          onClick={() => setAddingDomain(true)}
        >
          <Icon.Plus /> Add domain
        </button>
      </div>

      {/* Free subdomain card */}
      <div className="settings-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--coral-soft)', color: 'var(--coral-deep)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Drop />
          </span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Free subdomain</div>
            <div style={{ fontSize: 12, color: 'var(--ink-dim)' }}>Included with your workspace</div>
          </div>
          {subdomain && (
            <span className="row-tag olive" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon.Check /> Active
            </span>
          )}
        </div>
        {subdomain ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <code style={{ flex: 1, fontFamily: "'Geist Mono', ui-monospace, monospace", fontSize: 13, background: 'var(--bg)', border: '1px solid var(--line-strong)', borderRadius: 8, padding: '10px 12px', color: 'var(--ink)' }}>
              {subdomain}.linkflow.link
            </code>
            <button type="button" className="btn-outline" style={{ padding: '9px 12px' }} onClick={() => { navigator.clipboard.writeText(`${subdomain}.linkflow.link`); toast.success('Copied'); }} aria-label="Copy subdomain">
              <Icon.Copy />
            </button>
          </div>
        ) : (
          <button type="button" className="btn-outline" onClick={() => { window.location.href = '/onboarding/subdomain'; }}>
            Claim a free subdomain →
          </button>
        )}
      </div>

      {/* Add domain form */}
      {addingDomain && (
        <div className="settings-card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 14 }}>Add a custom domain</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <input
              type="text"
              style={inputStyle}
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
              placeholder="go.yourcompany.com"
              autoFocus
            />
            <button type="button" className="btn-coral" onClick={handleAddDomain} disabled={!newDomain.trim()} data-testid="add-domain-btn">Add</button>
            <button type="button" className="btn-outline" onClick={() => setAddingDomain(false)}>Cancel</button>
          </div>
          <p style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 12, fontFamily: "'Geist Mono', ui-monospace, monospace" }}>
            Add a <code style={{ background: 'var(--bg-warm)', padding: '1px 5px', borderRadius: 4 }}>CNAME</code> record pointing to{' '}
            <code style={{ background: 'var(--bg-warm)', padding: '1px 5px', borderRadius: 4 }}>cname.linkflow.app</code>{' '}
            in your DNS, then click Verify.
          </p>
        </div>
      )}

      {/* Custom domains list */}
      {loading ? (
        <div className="panel">
          <div className="ds-empty">
            <div className="ds-skeleton" style={{ height: 14, width: 160, marginBottom: 12 }} />
            <div className="ds-skeleton" style={{ height: 10, width: 240 }} />
          </div>
        </div>
      ) : domains.length === 0 ? (
        <div className="panel">
          <div className="ds-empty">
            <div className="ds-empty-icon">
              <Icon.Globe style={{ width: 22, height: 22 }} />
            </div>
            <div className="ds-empty-title">No custom domains yet</div>
            <p className="ds-empty-sub">
              Add your own domain to create branded short links like <code>go.company.com/spring</code>.
            </p>
          </div>
        </div>
      ) : (
        <div className="full-table-wrap">
          <div className="full-table-head" style={{ gridTemplateColumns: '2fr 1fr 1fr 140px' }}>
            <div>Domain</div>
            <div>Status</div>
            <div>Added</div>
            <div />
          </div>
          {domains.map((domain, i) => (
            <div
              key={domain.domain_id}
              className="full-table-row"
              style={{ gridTemplateColumns: '2fr 1fr 1fr 140px' }}
            >
              <div className="row-link">
                <div className={`row-favicon ${['', 'olive', 'dark', 'mustard'][i % 4]}`}>
                  {domain.domain.charAt(0).toUpperCase()}
                </div>
                <div className="row-info">
                  <div className="row-slug">{domain.domain}</div>
                  <div className="row-dest">{domain.verified ? 'CNAME verified · SSL active' : 'DNS pending'}</div>
                </div>
              </div>
              <div>
                {domain.verified ? (
                  <span className="row-tag olive" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.Check /> verified</span>
                ) : (
                  <span className="row-tag mustard" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.Clock /> pending</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-faint)', fontFamily: "'Geist Mono',monospace" }}>
                {domain.created_at ? new Date(domain.created_at).toLocaleDateString() : '—'}
              </div>
              <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                {!domain.verified && (
                  <button type="button" className="btn-outline" style={{ padding: '5px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => handleVerify(domain)} disabled={verifying[domain.domain_id]}>
                    <Icon.Refresh /> Verify
                  </button>
                )}
                <button type="button" className="row-more" style={{ color: 'var(--coral)' }} onClick={() => handleDelete(domain.domain_id)} title="Delete domain">
                  <Icon.Trash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
